import {
  ArrowUpRight,
  BookOpen,
  ThumbsDown as ThumbsDownIcon,
  ThumbsUp as ThumbsUpIcon,
} from 'lucide-react'
import {
  CodeEditor,
  CodeEditorHandler,
  ExtendedCMDiagnostic,
} from '@devbookhq/code-editor'
import { CodeSnippetState } from '@devbookhq/react'
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useRouter } from 'next/router'
import cn from 'classnames'
import debounce from 'lodash.debounce'
import path from 'path-browserify'

import { Tab } from 'utils/examplesContent'
import {
  analytics,
  editFileDebounce,
} from 'utils/analytics'
import { rootdir } from 'utils/constants'
import { saveUserFeedback } from 'utils/analytics/feedback'
import { useLanguageClients } from 'utils/useLanguageClients'
import { useSupportedLangaugesWithTextMate } from 'utils/languages'
import Email from 'components/Email'
import ExecutionButton from 'components/ExecutionButton'
import Feedback from 'components/Feedback'
import FileButtonPlayground from 'components/FileButtonPlayground'
import Rating, { RatingType } from 'components/Rating'

type RatingStep = 'feedback' | 'email'

export const defaultDBProvider = 'postgresql'
export const dbSchema = 'prisma/schema.prisma'

export interface Handler extends CodeEditorHandler {
  changeFile: (name: string, change: (content: string) => string) => void
}

export interface Props {
  exampleTitle: string
  tabs: Tab[]
  docsLink: string
  docsLinkTitle: string
  onCodeChange: (code: string, file: string) => void
  csState: CodeSnippetState
  onRunClick: () => void
  onStopClick: () => void
  isHidden?: boolean
  onDiagnosticsChange?: (diagnostics: ExtendedCMDiagnostic[]) => void
}

const Code = forwardRef<Handler, Props>(({
  exampleTitle,
  tabs: initialTabs,
  docsLink,
  docsLinkTitle,
  onCodeChange,
  csState,
  onRunClick,
  onStopClick,
  isHidden,
  onDiagnosticsChange,
}, ref) => {
  const router = useRouter()
  const [feedback, setFeedback] = useState('')
  const [email, setEmail] = useState('')
  const [ratingStep, setRatingStep] = useState<RatingStep>()
  const [rating, setRating] = useState<RatingType>('none')
  const [tabs, setTabs] = useState(initialTabs)
  const [selectedTabIdx, setSelectedTabIdx] = useState(0)
  const editorRef = useRef<CodeEditorHandler>(null)
  const selectedTab = useMemo(() => tabs[selectedTabIdx], [selectedTabIdx, tabs])
  const [tabInitCode, setTabInitCode] = useState(selectedTab.code)
  const languageClients = useLanguageClients()
  const supportedLanguages = useSupportedLangaugesWithTextMate()

  console.log('selectedTab', selectedTab)

  useEffect(function switchTabContent() {
    setTabInitCode(selectedTab.code)
  }, [selectedTabIdx, selectedTab])

  useEffect(function updateCode() {
    onCodeChange(tabs[selectedTabIdx].code,
      tabs[selectedTabIdx].name)
  }, [
    tabs,
    selectedTabIdx,
    onCodeChange,
  ])

  useEffect(function reinitialize() {
    setTabs(initialTabs)
    setSelectedTabIdx(0)
  }, [initialTabs])

  const contentChangeAnalytics = useMemo(() => debounce((code: string, filename: string) => {
    analytics.track('file edited', {
      code,
      filename,
    })
  }, editFileDebounce, {
    leading: false,
    trailing: true,
  }), [])

  const handleCodeChange = useCallback((code: string) => {
    setTabs(tabs => {
      const newTabs = [...tabs]
      newTabs[selectedTabIdx].code = code
      contentChangeAnalytics(code, newTabs[selectedTabIdx].name)
      return newTabs
    })
  }, [selectedTabIdx, contentChangeAnalytics])

  const focus = useCallback(() => {
    editorRef.current?.focus()
  }, [])

  const handleEditorCopy = useCallback((code: string, startLine: number) => {
    analytics.track('editor selection copied', {
      code,
      startLine,
    })
  }, [])

  const getSelection = useCallback(() => editorRef.current?.getSelection(), [])

  const getDiagnostics = useCallback(() => {
    return editorRef.current?.getDiagnostics()
  }, [])

  const changeFile: Handler['changeFile'] = useCallback((name, change) => {
    setTabs(tabs => {
      const tabIndex = tabs.findIndex(t => t.name === name)
      if (tabIndex >= 0) {
        const newTabs = [...tabs]
        newTabs[tabIndex].code = change(newTabs[tabIndex].code)
        if (tabIndex === selectedTabIdx) {
          setTabInitCode(newTabs[tabIndex].code)
        }
        return newTabs
      } else {
        return tabs
      }
    })
  }, [selectedTabIdx])

  const handleEnter = useCallback(() => {
    onRunClick()
    analytics.track('code executed with enter', { files: tabs })
  }, [onRunClick, tabs])

  const handleRunClick = useCallback(() => {
    onRunClick()
    analytics.track('code executed with run click', { files: tabs })
  }, [onRunClick, tabs])

  const handleDocsLinkClick = useCallback((docsLink: string) => {
    analytics.track('Prisma client example docs link clicked', { docsLink })
  }, [])

  const rate = useCallback((rating: RatingType) => {
    setRating(rating)
    setRatingStep('feedback')

    saveUserFeedback({
      rating,
      codeExampleTitle: exampleTitle,
      codeExamplePath: router.asPath,
    })
    analytics.track('code example rated', {
      rating,
      codeExampleTitle: exampleTitle,
      codeExamplePath: router.asPath,
    })
  }, [router, exampleTitle])

  const sendFeedback = useCallback((e: MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (!feedback) return

    saveUserFeedback({
      rating,
      codeExampleTitle: exampleTitle,
      codeExamplePath: router.asPath,
    }, feedback)
    analytics.track('code example feedback sent', {
      rating,
      codeExampleTitle: exampleTitle,
      codeExamplePath: router.asPath,
    })

    setRatingStep('email')
  }, [
    feedback,
    router,
    rating,
    exampleTitle,
  ])

  const saveEmail = useCallback((e: MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (!email) return

    saveUserFeedback({
      rating,
      codeExampleTitle: exampleTitle,
      codeExamplePath: router.asPath,
      email,
    }, feedback)
    analytics.track('user feedback email submitted (code example rating)', {
      rating,
      codeExampleTitle: exampleTitle,
      codeExamplePath: router.asPath,
      email,
    })

    setRatingStep(undefined)
    setEmail('')
    setFeedback('')
  }, [
    email,
    router,
    rating,
    feedback,
    exampleTitle,
  ])

  useImperativeHandle(
    ref,
    () => ({
      focus,
      changeFile,
      getDiagnostics,
      getSelection,
    }),
    [
      focus,
      getSelection,
      changeFile,
      getDiagnostics,
    ],
  )

  return (
    <div className={cn(
      'h-full',
      'lg:h-auto',
      'w-full',
      'flex',
      'flex-col',
      'flex-1',
      'lg:border-r',
      'lg:border-gray-300',
      'lg:rounded-tl-xl',
      'lg:rounded-bl-xl',
      { hidden: isHidden }
    )}>
      <div
        className="
        flex
        flex-row
        items-center
        border-b
        border-gray-300
        bg-gray-100
        py-0.5
        px-2
        space-x-1
        lg:p-1
      "
      >
        <ExecutionButton
          state={csState}
          onRunClick={handleRunClick}
          onStopClick={onStopClick}
        />
        {tabs.map((t, i) => (
          <FileButtonPlayground
            isSelected={selectedTabIdx === i}
            key={t.name}
            text={t.name}
            onClick={() => {
              setSelectedTabIdx(i)
              setTabInitCode(tabs[i].code)
            }}
          />
        ))}
      </div>
      <div className="
        text-sm
        text-indigo-600
        hover:text-indigo-500
        bg-gray-100
        flex
        items-center
        justify-between
        border-b
        border-gray-300
      ">
        <div className="
          p-2
          flex
          items-center
        ">
          <BookOpen
            size={16}
          />
          <div className="
            ml-2
            mr-1
            border-b
            border-indigo-600
          ">
            <a
              href={docsLink}
              rel="noopener noreferrer"
              target="_blank"
              onClick={() => handleDocsLinkClick(docsLink)}
            >
              Go to <span className="font-bold">{docsLinkTitle}</span> docs
            </a>
          </div>
          <ArrowUpRight
            size={16}
          />
        </div>

        <div className="
          relative
          z-50
          bg-gray-200
          p-2
        ">
          {ratingStep === 'feedback' &&
            <Feedback
              position="left"
              title={rating === 'up' ? 'What else could we improve?' : 'What didn\'t you like?'}
              value={feedback}
              onChange={e => setFeedback(e.target.value)}
              onClickOutside={() => setRatingStep(undefined)}
              onSubmit={sendFeedback}
            />
          }
          {ratingStep === 'email' &&
            <Email
              position="left"
              value={email}
              onChange={e => setEmail(e.target.value)}
              onClickOutside={() => setRatingStep(undefined)}
              onSubmit={saveEmail}
            />
          }
          <Rating
            ratingButtonsColor="text-gray-500"
            title="Rate this template"
            titleColor="text-gray-600"
            value={rating}
            onRatingDownClick={() => rate('down')}
            onRatingUpClick={() => rate('up')}
          />
        </div>
      </div>
      <div
        className="
          relative
          flex-1
          overflow-hidden
        "
      >
        <CodeEditor
          className="absolute inset-0"
          content={tabInitCode}
          filename={path.join(rootdir, selectedTab.name)}
          handleRun={handleEnter}
          isReadOnly={selectedTab.name.includes('schema.prisma')}
          languageClients={languageClients}
          ref={editorRef}
          supportedLanguages={supportedLanguages}
          autofocus
          onContentChange={handleCodeChange}
          onCopy={handleEditorCopy}
          onDiagnosticsChange={onDiagnosticsChange}
        />
      </div>
    </div>
  )
})

Code.displayName = 'Code'

export default Code
